import { publish } from '~/event-bus';
import { formatMoney, getCartSectionNames } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';

/**
 * Adds the gift wrap product as a nested cart line under the selected product.
 * - Wraps any unwrapped quantity of the product in the cart first.
 * - Otherwise adds the quantity selector amount of both the product and the gift wrap.
 */
customElements.define(
  'product-gift-wrap',
  class ProductGiftWrap extends HTMLCustomElement {
    cart = null;

    get button() {
      return this.targets.button;
    }

    get variantId() {
      return Number(this.getAttribute('variant'));
    }

    get price() {
      return Number(this.getAttribute('price'));
    }

    get parentForm() {
      return this.closest('product-form');
    }

    /**
     * Returns the selected product's top-level cart line.
     * @returns {Object|undefined}
     */
    get parentLine() {
      const parentVariantId = this.parentForm.getSelectedVariantId();

      return this.cart?.items.find((line) => line.variant_id === parentVariantId && !line.parent_relationship);
    }

    /**
     * Returns the quantity of the product in the cart without gift wrap.
     * @returns {Number}
     */
    get unwrappedQuantity() {
      const parentLine = this.parentLine;

      if (!parentLine) return 0;

      const wrappedQuantity = this.cart.items
        .filter((line) => line.variant_id === this.variantId && line.parent_relationship?.parent_key === parentLine.key)
        .reduce((total, line) => total + line.quantity, 0);

      return Math.max(parentLine.quantity - wrappedQuantity, 0);
    }

    /**
     * Returns the quantity selector value.
     * @returns {Number}
     */
    get selectedQuantity() {
      const input = this.parentForm.querySelector('[data-target="product-form.quantity"]');

      return Math.max(Number(input?.value) || 1, 1);
    }

    /**
     * Returns the gift wrap quantity to add.
     * @returns {Number}
     */
    get quantity() {
      return this.unwrappedQuantity || this.selectedQuantity;
    }

    /**
     * Returns the line item properties linking the gift wrap to the product.
     * @returns {Object|undefined}
     */
    get properties() {
      const propertyName = this.getAttribute('property-name');
      const options = this.parentForm.getSelectedVariant()?.options?.filter((option) => option !== 'Default Title');
      const parentTitle = [this.getAttribute('parent-title'), options?.join(' / ')].filter(Boolean).join(' - ');

      if (!propertyName || !parentTitle) return;

      return { [propertyName]: parentTitle };
    }

    connectedCallback() {
      if (super.connectedCallback) super.connectedCallback();

      if (!this.button || !this.parentForm) return;

      this.refresh();

      this.subscribe('cart:update', this.refresh.bind(this));
      this.on('variant', this.parentForm, this.render.bind(this));

      this.on('change', this.parentForm, (event) => {
        if (event.target.dataset.target === 'product-form.quantity') this.render();
      });

      this.on('click', this.button, this.handleClick.bind(this));
    }

    /**
     * Fetches the latest cart and re-renders.
     */
    async refresh() {
      try {
        this.cart = await this.fetchJSON(`${theme.routes.cart_url}.js`);
      } catch (error) {
        console.error(error);
      }

      this.render();
    }

    /**
     * Renders the gift wrap total and button label for the current quantity.
     */
    render() {
      const quantity = this.quantity;
      const amount = this.price * quantity;

      if (this.targets.price) {
        /**
         * Matches `money_without_trailing_zeros`, keeping two decimals when there are pence.
         */
        this.targets.price.textContent =
          amount % 100 === 0
            ? formatMoney(amount)
            : new Intl.NumberFormat(`${Shopify.locale}-${Shopify.country}`, {
                style: 'currency',
                currency: Shopify.currency.active,
              }).format(amount / 100);
      }

      if (!this.button.disabled) {
        this.button.textContent = this.getLabel(quantity);
      }
    }

    /**
     * Returns the button label for the given quantity.
     * @param {Number} quantity - The gift wrap quantity.
     * @returns {String}
     */
    getLabel(quantity) {
      const quantityLabel = this.getAttribute('quantity-label');

      if (quantity > 1 && quantityLabel) return quantityLabel.replace('[quantity]', quantity);

      return theme.strings.sections.main_product.add_to_cart;
    }

    /**
     * Posts JSON to a cart endpoint and returns the response.
     * @param {String} url - The endpoint.
     * @param {Object} body - The request body.
     * @returns {Promise<Object>}
     */
    async fetchJSON(url, body) {
      const response = await fetch(url, {
        ...(body && {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }),
      }).then((response) => response.json());

      if (response.status) throw new Error(response.description ?? response.message);

      return response;
    }

    /**
     * Increases the product's cart line, keeping any personalisation cost line in sync.
     * @param {Object} parentLine - The product's cart line.
     * @param {Number} quantity - The quantity to add.
     * @param {Array} sections - The sections to render.
     * @returns {Promise<Object>}
     */
    async increaseParentLine(parentLine, quantity, sections) {
      const updates = { [parentLine.key]: parentLine.quantity + quantity };
      const personalisationLine = this.cart.items.find(
        (line) => line.properties?._personalisation_key === parentLine.key,
      );

      if (personalisationLine) updates[personalisationLine.key] = parentLine.quantity + quantity;

      const response = await this.fetchJSON(`${theme.routes.cart_update_url}.js`, { updates, sections });

      this.cart = response;

      return response;
    }

    async handleClick() {
      try {
        this.button.disabled = true;
        this.button.textContent = theme.strings.sections.main_product.adding_to_cart;

        await this.refresh();

        const sections = getCartSectionNames();
        const quantity = this.quantity;
        const item = { id: this.variantId, properties: this.properties };
        let parentLine = this.parentLine;
        let response;

        if (!parentLine) {
          const parentVariantId = this.parentForm.getSelectedVariantId();

          response = await this.fetchJSON(`${theme.routes.cart_add_url}.js`, {
            items: [
              { id: parentVariantId, quantity },
              { ...item, quantity, parent_id: parentVariantId },
            ],
            sections,
          });
        } else {
          /**
           * Every product is already wrapped, so add more of the product first.
           */
          if (!this.unwrappedQuantity) {
            response = await this.increaseParentLine(parentLine, quantity, sections);
            parentLine = this.parentLine;
          }

          if (this.unwrappedQuantity) {
            response = await this.fetchJSON(`${theme.routes.cart_add_url}.js`, {
              items: [{ ...item, quantity: this.unwrappedQuantity, parent_line_key: parentLine.key }],
              sections,
            });
          }
        }

        publish('cart:update', { sections: response.sections });
        publish('cart:added');

        this.button.textContent = this.getAttribute('added-text') ?? '';

        setTimeout(() => this.resetButtonState(), 2000);
      } catch (error) {
        console.error(error);

        this.resetButtonState();
      }
    }

    /**
     * Resets the button state.
     */
    resetButtonState() {
      this.button.disabled = false;
      this.render();
    }
  },
);
